/**
 * Chainage-origin helpers for the forward-plotting log.
 *
 * The unrolled tunnel log is drawn in canvas space where x = `padding`
 * corresponds to the tunnel's Start Chainage (`startrd`) — NOT chainage 0.
 * Before this offset existed the scale always started at 0 and a discontinuity
 * (validated to lie within [startrd, endrd]) was anchored at `padding +
 * rd*multiplier`, which fell off the right of the log and was clipped away
 * whenever startrd was non-zero.
 *
 * These pure helpers convert between the three coordinate systems so every
 * renderer applies the Start-Chainage offset consistently:
 *
 *   - chainage (m)     what the user enters / reads   (absolute, e.g. 150)
 *   - canvas X (px)    where it is drawn              (padding + (rd-startrd)*mult)
 *   - scale label (m)  tick text on the axis          (absolute, rounded)
 *
 * `chainageToCanvasX` and `canvasXToChainage` are exact inverses, which keeps a
 * trace's reported chainage equal to the chainage it was anchored at.
 *
 * With startrd = 0 every helper reduces to the original expression, so existing
 * behaviour (and all prior validation) is unchanged.
 */

// Normalise the raw `startrd` field (may be a number, a string, "" or
// undefined when a trace predates the feature) to a usable numeric offset.
export const toStartOffset = (startrd) => Number(startrd) || 0;

// Recommended maximum tunnel log length (m). Beyond this the unrolled log gets
// too long to read comfortably (ideal is 100-150 m). This is a soft guideline,
// not a hard limit — the log still plots past it.
export const MAX_LOG_LENGTH = 200;

// Soft, non-blocking warning surfaced on the input page when the chainage span
// exceeds MAX_LOG_LENGTH. The trace still plots; the user is nudged to trim the
// End Chainage.
export const LOG_LENGTH_ALERT =
  "Log length is more than 200m. Ideal length is max 100-150m. Consider editing end chainage.";

// True when the log length (End Chainage - Start Chainage) exceeds the
// recommended maximum. Tolerant of string / blank fields like the other helpers.
export const exceedsMaxLogLength = (startrd, endrd) =>
  Number(endrd) - toStartOffset(startrd) > MAX_LOG_LENGTH;

// Shortest log (m) the plot lays out legibly. Below this the line labels, the
// direction captions and the cross-section legend crowd the log and overlap.
export const MIN_LOG_LENGTH = 40;

export const shortLogError = (startrd) =>
  `Please select End Ch. as ≥ (Start Ch. + ${MIN_LOG_LENGTH}) = ${toStartOffset(startrd) + MIN_LOG_LENGTH}`;

export const isShortLog = (startrd, endrd) =>
  Number(endrd) - toStartOffset(startrd) < MIN_LOG_LENGTH;

// Absolute chainage (m) printed on the scale tick at canvas x = `i`.
export const scaleLabelChainage = (i, padding, multiplier, startOffsetRD = 0) =>
  Math.round((i - padding) / multiplier + startOffsetRD);

// Canvas x (px) where an absolute chainage `rd` (m) is anchored.
export const chainageToCanvasX = (rd, padding, multiplier, startOffsetRD = 0) =>
  padding + (rd - startOffsetRD) * multiplier;

// Absolute chainage (m) reported back from a canvas x position.
export const canvasXToChainage = (x, padding, multiplier, startOffsetRD = 0) =>
  (x - padding) / multiplier + startOffsetRD;
